/* The storefront guard is a checkout aid, not a server-side pricing rule. */
(() => {
  if (globalThis.StudioCartUpgrades) return;
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const UPGRADE_VARIANT = '71355350974717';
  const id = value => /^[1-9][0-9]{0,23}$/.test(String(value ?? '')) ? String(value) : '';
  const props = item => item?.properties && typeof item.properties === 'object' ? item.properties : {};
  const token = item => props(item)['_Studio submission'];
  const linked = item => props(item)['_Studio upgrade for'];
  const parentKey = item => item?.parent_relationship?.parent_key;
  const quantity = value => Number.isSafeInteger(value) && value >= 1;
  const fail = code => { throw Object.assign(new Error(code), { code }); };
  const parentCandidate = item => Boolean(props(item)['_Studio second side'] || props(item)['_Studio upgrade variant']);
  const childCandidate = item => Boolean(linked(item)) || id(item?.variant_id ?? item?.id) === UPGRADE_VARIANT;
  const priceMatches = (item, config) => item.original_price === config.price && item.price === config.price;
  const configured = config => config.id === UPGRADE_VARIANT && config.price === 1000 && config.currency === 'USD' && config.available === true && config.variantCount === 1 && config.requiresShipping === false;
  function itemsOf(cart) {
    if (!cart || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0 || typeof cart.currency !== 'string' || !Array.isArray(cart.items) || cart.items.length > 2000 || cart.items.some(item => !item || typeof item.key !== 'string' || !item.key || !quantity(item.quantity))) fail('unavailable');
    if (new Set(cart.items.map(item => item.key)).size !== cart.items.length) fail('unavailable');
    return cart.items;
  }
  function analyze(cart, config) {
    const items = itemsOf(cart), parents = items.filter(parentCandidate), children = items.filter(childCandidate);
    const relevant = Boolean(parents.length || children.length), issues = [], pairs = [], orphans = [], repairable = [];
    const add = (code, parent, child) => issues.push({ code, parent, child });
    if (!relevant) return { relevant, valid: true, issues, pairs, orphans, parents, repairable };
    if (!configured(config) || cart.currency !== config.currency) add('configuration');
    for (const parent of parents) {
      const reference = token(parent), matches = children.filter(child => linked(child) === reference);
      const sameToken = items.filter(item => token(item) === reference);
      if (!UUID.test(reference || '') || sameToken.length !== 1 || props(parent)['_Studio second side'] !== 'true' || id(props(parent)['_Studio upgrade variant']) !== config.id || !props(parent)['_Engraving files'] || !props(parent)['_Design file']) { add('parent', parent); continue; }
      if (matches.length !== 1) {
        add(matches.length ? 'ambiguous' : 'missing', parent);
        // Never add beside a conflicting native child, even if its metadata was lost.
        if (!matches.length && configured(config) && cart.currency === config.currency && !items.some(child => parentKey(child) === parent.key)) repairable.push(parent);
        continue;
      }
      const child = matches[0];
      if (child === parent || id(child.variant_id ?? child.id) !== config.id || parentKey(child) !== parent.key || !priceMatches(child, config)) { add('relationship', parent, child); continue; }
      const pair = { parent, child }; pairs.push(pair);
      if (child.quantity !== parent.quantity) add('quantity', parent, child);
    }
    for (const child of children) {
      if (!parents.some(parent => token(parent) === linked(child) && parentKey(child) === parent.key)) add('orphan', null, child);
      // Removal is explicit and only for the actual configured fee with no parent
      // by either native relation or studio identity. Never detach another add-on.
      if (configured(config) && cart.currency === config.currency && id(child.variant_id ?? child.id) === config.id && priceMatches(child, config)
        && !items.some(item => item !== child && (item.key === parentKey(child) || (linked(child) && token(item) === linked(child))))) orphans.push(child);
    }
    return { relevant, valid: issues.length === 0, issues, pairs, orphans, parents, repairable };
  }
  function planQuantity(cart, config, identity, nextQuantity) {
    if (!Number.isSafeInteger(nextQuantity) || nextQuantity < 0) fail('quantity_blocked');
    const state = analyze(cart, config), matches = cart.items.filter(item => item.key === identity.key && token(item) === identity.submission && id(item.variant_id ?? item.id) === identity.variantId);
    if (matches.length !== 1) fail('quantity_blocked');
    const parent = matches[0], pair = state.pairs.find(value => value.parent === parent);
    if (nextQuantity === 0 && parentCandidate(parent) && UUID.test(token(parent)) && cart.items.filter(item => token(item) === token(parent)).length === 1 && props(parent)['_Engraving files'] && props(parent)['_Design file']) {
      return { updates: { [parent.key]: 0, ...(pair ? { [pair.child.key]: 0 } : {}) }, submission: token(parent), parentVariantId: id(parent.variant_id ?? parent.id), nextQuantity };
    }
    if (!configured(config) || cart.currency !== config.currency) fail('quantity_blocked');
    if (!pair || state.issues.some(issue => issue.parent === parent && issue.code !== 'quantity')) fail('quantity_blocked');
    // Existing quantity mismatch may be repaired by the requested parent quantity;
    // no missing fee is created, and no unrelated cart key enters this update.
    return { updates: { [parent.key]: nextQuantity, [pair.child.key]: nextQuantity }, submission: token(parent), parentVariantId: id(parent.variant_id ?? parent.id), nextQuantity };
  }
  function editUrl(parent, origin) {
    try {
      const url = new URL(parent?.url, origin);
      if (url.origin !== origin || url.username || url.password || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?products\/[a-z0-9][a-z0-9-]*$/i.test(url.pathname) || !id(parent.variant_id ?? parent.id) || typeof parent.key !== 'string' || parent.key.length > 500) return null;
      url.search = ''; url.hash = ''; url.searchParams.set('view', 'studio-customize'); url.searchParams.set('variant', id(parent.variant_id ?? parent.id)); url.searchParams.set('studio_cart_line', parent.key);
      return `${url.pathname}${url.search}`;
    } catch { return null; }
  }
  // Older mobile browsers have AbortController but no AbortSignal.timeout.
  function requestDeadline(milliseconds) {
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') return { signal: AbortSignal.timeout(milliseconds), clear() {} };
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), milliseconds);
    return { signal: controller.signal, clear: () => clearTimeout(timer) };
  }
  function createClient({ config, fetchImpl = fetch, root = '/', changed = () => {}, storage }) {
    let pending = false;
    try { if (storage === undefined) storage = globalThis.sessionStorage; } catch { /* Repair fails closed without durable storage. */ }
    const repairKey = 'je-studio-upgrade-repairs-v1';
    let repairs = [], repairStorageReady = false;
    function loadRepairs() {
      if (!storage) fail('repair_pending');
      const values = JSON.parse(storage.getItem(repairKey) || '[]');
      if (!Array.isArray(values) || values.length > 1000 || values.some(entry => !entry || !UUID.test(entry.token) || typeof entry.key !== 'string' || !entry.key || entry.key.length > 500 || !id(entry.variantId) || entry.upgradeId !== UPGRADE_VARIANT || !quantity(entry.quantity) || !['requested', 'rejected'].includes(entry.status)) || new Set(values.map(entry => entry.token)).size !== values.length) fail('repair_pending');
      return values;
    }
    function saveRepairs(values) {
      if (!storage) fail('repair_pending');
      const encoded = JSON.stringify(values); storage.setItem(repairKey, encoded);
      if (storage.getItem(repairKey) !== encoded) fail('repair_pending');
      repairs = values; repairStorageReady = true;
    }
    function reconcileRepairs(cart) {
      try {
        const current = loadRepairs(), state = analyze(cart, config);
        const remaining = current.filter(entry => {
          const parents = cart.items.filter(item => token(item) === entry.token), children = cart.items.filter(item => linked(item) === entry.token);
          const pair = state.pairs.find(value => token(value.parent) === entry.token && value.parent.key === entry.key && id(value.parent.variant_id ?? value.parent.id) === entry.variantId && value.parent.quantity === entry.quantity && value.child.quantity === entry.quantity);
          if (parents.length === 1 && children.length === 1 && pair) return false;
          return !(entry.status === 'rejected' && !children.length);
        });
        if (remaining.length !== current.length) saveRepairs(remaining); else { repairs = current; repairStorageReady = true; }
      } catch { repairStorageReady = false; }
    }
    const endpoint = path => `${root.endsWith('/') ? root : `${root}/`}${path}`;
    async function json(url, options = {}) {
      const deadline = requestDeadline(15000);
      try {
        const response = await fetchImpl(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: deadline.signal });
        let value; try { value = await response.json(); } catch { fail('unavailable'); }
        if (!response.ok || value?.errors || value?.status >= 400) fail('quantity_error');
        itemsOf(value); return value;
      } finally { deadline.clear(); }
    }
    const read = async () => { const cart = await json(endpoint('cart.js')); reconcileRepairs(cart); return cart; };
    async function write(updates, sections, sectionsUrl) {
      const result = await json(endpoint('cart/update.js'), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ updates, ...(sections?.length ? { sections, sections_url: sectionsUrl } : {}) }) });
      changed(result); return result;
    }
    return {
      read,
      get pending() { return pending; },
      repairBlocked: parent => !repairStorageReady || repairs.some(entry => entry.token === token(parent)),
      get pendingRepair() { return repairs.length > 0; },
      async update(identity, nextQuantity, sections, sectionsUrl) {
        if (pending) fail('busy'); pending = true;
        try {
          const current = await read(), plan = planQuantity(current, config, identity, nextQuantity);
          const result = await write(plan.updates, sections, sectionsUrl), state = analyze(result, config);
          const parents = result.items.filter(item => token(item) === plan.submission), children = result.items.filter(item => linked(item) === plan.submission);
          if (nextQuantity === 0) {
            if (parents.length || children.length) fail('quantity_error');
            // Removing the native parent makes a late nested add invalid. Clear
            // only this confirmed removal's repair identity, never other drafts.
            try { saveRepairs(loadRepairs().filter(entry => entry.token !== plan.submission)); } catch { /* A retained journal is conservative. */ }
          }
          else {
            const pair = state.pairs.find(value => token(value.parent) === plan.submission);
            if (!pair || parents.length !== 1 || children.length !== 1 || id(pair.parent.variant_id ?? pair.parent.id) !== plan.parentVariantId || pair.parent.quantity !== nextQuantity || pair.child.quantity !== nextQuantity || state.issues.some(issue => issue.parent === pair.parent)) fail('quantity_error');
          }
          return result;
        } finally { pending = false; }
      },
      async removeOrphan(key) {
        if (pending) fail('busy'); pending = true;
        try {
          const cart = await read(), orphan = analyze(cart, config).orphans.find(item => item.key === key);
          if (!orphan) fail('quantity_blocked');
          const result = await write({ [orphan.key]: 0 });
          if (result.items.some(item => item.key === orphan.key)) fail('quantity_error');
          return result;
        } finally { pending = false; }
      },
      async addMissing(key) {
        if (pending) fail('busy'); pending = true;
        let addDeadline;
        try {
          const cart = await read(), parent = analyze(cart, config).repairable.find(item => item.key === key);
          if (!parent) fail('quantity_blocked');
          if (!repairStorageReady || repairs.some(entry => entry.token === token(parent))) fail('repair_pending');
          const attempt = { key: parent.key, token: token(parent), variantId: id(parent.variant_id ?? parent.id), upgradeId: config.id, quantity: parent.quantity, status: 'requested' };
          // Persist before sending. Absence after a timeout is not permission to
          // replay: the first request might still finish after that cart read.
          saveRepairs([...repairs, attempt]);
          // This is an explicitly requested purchase. An uncertain POST is never replayed.
          addDeadline = requestDeadline(15000);
          const response = await fetchImpl(endpoint('cart/add.js'), { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, signal: addDeadline.signal, body: JSON.stringify({ items: [{ id: config.id, quantity: parent.quantity, parent_line_key: parent.key, properties: { '_Studio upgrade for': token(parent) } }] }) });
          if (!response.ok) {
            let rejection;
            try { rejection = await response.json(); } catch { /* Proxy/HTML errors cannot establish rejection. */ }
            if (response.status === 422 && rejection && !Array.isArray(rejection) && rejection.status === 422 && typeof rejection.message === 'string' && rejection.message.trim() && typeof rejection.description === 'string' && rejection.description.trim()) {
              saveRepairs(repairs.map(entry => entry.token === attempt.token ? { ...entry, status: 'rejected' } : entry)); await read();
            }
            fail('add_error');
          }
          const result = await read(), state = analyze(result, config), pair = state.pairs.find(value => token(value.parent) === token(parent));
          changed(result);
          if (!pair || state.issues.some(issue => issue.parent === pair.parent) || pair.parent.quantity !== parent.quantity) fail('add_error');
          return result;
        } finally { addDeadline?.clear(); pending = false; }
      }
    };
  }
  const api = { analyze, planQuantity, editUrl, createClient };
  globalThis.StudioCartUpgrades = api;
  if (typeof document === 'undefined') return;

  let client, config, strings, state, latestCart, reading, busy = false, uncertain = false, initialized = false, refreshQueued = false, allowSubmit = false;
  const all = selector => [...document.querySelectorAll(selector)];
  function initialize() {
    if (initialized) return true;
    const source = document.querySelector('[data-studio-upgrade-config]'); if (!source) return false;
    try { const data = JSON.parse(source.textContent); config = data.config; strings = data.strings; } catch { return false; }
    client = createClient({ config, root: window.Shopify?.routes?.root || '/', changed: cart => { state = analyze(cart, config); latestCart = cart; globalThis.StudioCartApproval?.accept(cart); } });
    initialized = true; return true;
  }
  function text(key) { return strings?.[key] || window.cartStrings?.error || 'Check your cart before checkout.'; }
  function relevant() { return state?.relevant || all('[data-studio-upgrade-guard]').some(node => node.dataset.required === 'true'); }
  function activeCartForm() { return document.querySelector('cart-drawer.active #CartDrawer-Form') || document.getElementById('cart') || document.getElementById('CartDrawer-Form'); }
  function quantityWorkPending() { return all('cart-items, cart-drawer-items').some(node => node.quantityUpdatePending); }
  function formMatchesCart(form) {
    if (!latestCart || !form) return false;
    const rows = [...form.querySelectorAll('[data-line-key]')];
    if (rows.length !== latestCart.items.length) return false;
    const seen = new Set();
    return rows.every(row => {
      const item = latestCart.items.find(value => value.key === row.dataset.lineKey), input = row.querySelector('[name="updates[]"]');
      if (!item || seen.has(item.key) || !input || !String(input.value).trim() || Number(input.value) !== item.quantity) return false;
      seen.add(item.key); return true;
    });
  }
  function paint(message) {
    if (!initialize()) return;
    const form = activeCartForm(), quantitiesPending = quantityWorkPending(), quantitiesDiffer = Boolean(latestCart && form && !formMatchesCart(form));
    const blocked = relevant() && (busy || reading || uncertain || quantitiesPending || quantitiesDiffer || !state?.valid || message);
    for (const button of all('[data-studio-upgrade-checkout]')) {
      if (blocked) { button.disabled = true; button.dataset.studioUpgradeBlocked = 'true'; }
      else if (button.dataset.studioUpgradeBlocked === 'true') { button.disabled = Boolean(state?.empty) || button.dataset.studioApprovalBlocked === 'true' || button.dataset.bulkOrderBlocked === 'true'; delete button.dataset.studioUpgradeBlocked; }
      if (button.dataset.bulkOrderBlocked === 'true') button.disabled = true;
    }
    for (const accelerated of all('[data-studio-upgrade-accelerated]')) { accelerated.hidden = Boolean(blocked) || accelerated.dataset?.studioApprovalHidden === 'true' || all('[name="checkout"]').some(button => button.dataset.bulkOrderBlocked === 'true'); accelerated.inert = accelerated.hidden; }
    for (const guard of all('[data-studio-upgrade-guard]')) {
      guard.hidden = !blocked;
      if (!blocked) { guard.dataset.required = 'false'; continue; }
      guard.querySelector('[data-studio-upgrade-message]').textContent = text(message || (busy || reading || quantitiesPending ? 'checking' : client.pendingRepair ? 'repair_pending' : uncertain ? 'unavailable' : !state?.valid ? 'invalid' : quantitiesDiffer ? 'checking' : 'invalid'));
      const retry = guard.querySelector('[data-studio-upgrade-retry]'); retry.hidden = Boolean(busy || reading); retry.disabled = Boolean(busy || reading);
      const links = guard.querySelector('[data-studio-upgrade-links]'); links.replaceChildren();
      const parents = new Map((state?.parents || []).map(parent => [parent.key, parent]));
      for (const parent of parents.values()) {
        const href = editUrl(parent, location.origin); if (!href) continue;
        const anchor = document.createElement('a'); anchor.className = 'link underlined-link'; anchor.href = href; anchor.textContent = `${text('edit')}: ${parent.product_title || parent.title || ''}`; links.append(anchor);
      }
      for (const orphan of state?.orphans || []) {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'button button--secondary'; button.textContent = text('remove_orphan'); button.dataset.studioUpgradeOrphan = orphan.key; button.disabled = busy || Boolean(reading); links.append(button);
      }
      for (const parent of state?.repairable || []) {
        const button = document.createElement('button'); button.type = 'button'; button.className = 'button button--secondary'; button.textContent = `${text('add')}: ${parent.product_title || parent.title || ''}`; button.dataset.studioUpgradeAdd = parent.key; button.disabled = busy || Boolean(reading) || uncertain || client.repairBlocked(parent); links.append(button);
      }
    }
  }
  async function refresh() {
    if (!initialize()) return false;
    if (reading) return reading;
    reading = client.read().then(cart => { state = { ...analyze(cart, config), empty: cart.items.length === 0 }; latestCart = cart; uncertain = false; return state.valid; }).catch(() => { uncertain = true; return false; }).finally(() => { reading = null; paint(); });
    paint(); return reading;
  }
  Object.assign(api, {
    identity(element, line) {
      const row = element.querySelector(`#CartItem-${line}`) || element.querySelector(`#CartDrawer-Item-${line}`);
      return row ? { key: row.dataset.lineKey, submission: row.dataset.studioSubmission, variantId: row.dataset.studioVariant, secondSide: row.dataset.studioSecondSide, upgradeFor: row.dataset.studioUpgradeFor } : null;
    },
    async update(identity, nextQuantity, sections, sectionsUrl) {
      if (!initialize()) fail('unavailable'); if (busy || reading) fail('busy'); busy = true; uncertain = false; paint();
      try { const result = await client.update(identity, nextQuantity, sections, sectionsUrl); state = { ...analyze(result, config), empty: result.items.length === 0 }; return result; }
      catch (error) { uncertain = true; throw error; }
      finally { busy = false; paint(); }
    },
    message: error => text(error?.code || 'quantity_error'),
    accept(cart) { if (!initialize()) return; try { state = { ...analyze(cart, config), empty: cart.items.length === 0 }; latestCart = cart; if (!busy) uncertain = false; paint(); } catch { refresh(); } },
    sync: () => { paint(); globalThis.StudioCartApproval?.sync(); },
    refresh
  });
  for (const name of ['input', 'change']) document.addEventListener(name, event => {
    if (event.target.matches?.('[name="updates[]"]') && relevant()) paint();
  }, true);
  window.addEventListener('pageshow', event => { if (event.persisted && relevant()) refresh(); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden && relevant()) refresh(); });
  document.addEventListener('click', async event => {
    const retry = event.target.closest('[data-studio-upgrade-retry]'), orphan = event.target.closest('[data-studio-upgrade-orphan]'), add = event.target.closest('[data-studio-upgrade-add]');
    if (!retry && !orphan && !add) return; event.preventDefault();
    if (busy || reading || !initialize()) return;
    if (retry) { if (await refresh()) location.reload(); return; }
    busy = true; paint();
    try { if (add) await client.addMissing(add.dataset.studioUpgradeAdd); else await client.removeOrphan(orphan.dataset.studioUpgradeOrphan); location.reload(); }
    catch { uncertain = true; }
    finally { busy = false; paint(); }
  });
  document.addEventListener('submit', async event => {
    if (!['cart', 'CartDrawer-Form'].includes(event.target.id) || event.submitter?.name !== 'checkout' || !initialize() || !relevant()) return;
    if (allowSubmit) { allowSubmit = false; return; }
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy || client.pending || uncertain) { paint(); return; }
    const submitter = event.submitter;
    api.checkingCheckout = true;
    try {
      if (await refresh()) {
        if (quantityWorkPending() || !formMatchesCart(event.target)) { paint('quantity_error'); return; }
        // Dawn has already committed quantity changes through Ajax. Posting stale
        // index-based updates[] fields here could alter a freshly verified cart.
        const quantities = [...event.target.querySelectorAll('[name="updates[]"]')].filter(input => !input.disabled);
        quantities.forEach(input => { input.disabled = true; });
        allowSubmit = true;
        try { event.target.requestSubmit(submitter); }
        finally { allowSubmit = false; quantities.forEach(input => { input.disabled = false; }); }
      }
    } finally {
      api.checkingCheckout = false;
      if (!globalThis.JaxBulkCart?.checkingCheckout) { globalThis.StudioCartApproval?.finishCheckout(); globalThis.JaxBulkCart?.finishCheckout?.(); }
    }
  }, true);
  function boot() {
    if (!initialize()) return;
    if (relevant()) refresh();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, event => event.cartData?.items ? api.accept(event.cartData) : refresh());
    new MutationObserver(records => {
      if (refreshQueued || !records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-studio-upgrade-guard],[data-studio-upgrade-checkout]') || node.querySelector?.('[data-studio-upgrade-guard],[data-studio-upgrade-checkout]'))))) return;
      refreshQueued = true; queueMicrotask(() => { refreshQueued = false; if (relevant()) refresh(); else paint(); });
    }).observe(document.body, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true }); else boot();
})();

